import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Plus, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { sameValue } from '../hooks/useDirtyForm';
import { registerDirtySource } from '../utils/unsavedChanges';
import { API_BASE_URL } from './config/api';
import { storeAPI } from '../services/api';
import { useToast } from './shared/Toast';
import ProductMappingModal from './ProductMappingModal';
import LaasMileConfig from './LaasMileConfig';
import WoltDriveConfig from './WoltDriveConfig';
import { storeNameMappingsAPI } from '../services/platformMappingApi';
import UnsavedChangesBar from './settings/UnsavedChangesBar';
import { LoadFailed, SwitchRow } from './settings/instantSettings';
import { SettingsSheet, SheetBlock, SheetField, SheetRow, SheetSection, sectionDomId } from './settings/sheet';
import { SettingsSwitch, StoreLoadState } from './settings/storeSettingsForm';
import { ASIDE_BTN, GHOST_BTN, INPUT_CLS, StatusChip } from './shared/listPage';
import { EFOOD_INTEGRATOR } from './platformDefaults';

/**
 * Κατάλογος → «Πλατφόρμες» στη διάταξη «Φύλλο» (14/10/2026): μία ενότητα ανά πλατφόρμα παραγγελιών
 * (e-food, BOX, Wolt), μία για την αντιστοίχιση προϊόντων και μία για τις υπηρεσίες παράδοσης (Laas Mile,
 * Wolt Drive) — ως τότε πέντε καρτέλες, με τη μία ορατή κάθε φορά. Φύλλο και όχι λίστα με συρτάρι: είναι
 * ρυθμίσεις για τρεις σταθερές πλατφόρμες (όχι εγγραφές που μεγαλώνουν), ο ιδιοκτήτης βλέπει με μια ματιά
 * ποια είναι ενεργή, και το παράθυρο αντιστοίχισης ανοίγει πάνω από τη σελίδα, όχι πάνω από συρτάρι.
 *
 * Ίδια λογική με πριν:
 *  - ο διακόπτης κάθε πλατφόρμας σώζεται αμέσως: PUT …/platform-flags {platform_x} και, στην ενεργοποίηση,
 *    PUT …/platforms/:x με τα τρέχοντα στοιχεία (χωρίς κενό κωδικό/κλειδί — θα έσβηνε τα αποθηκευμένα)·
 *  - τα στοιχεία, η ιδιόκτητη παράδοση και οι χρόνοι σώζονται με την «Αποθήκευση» (εδώ: η μπάρα αλλαγών,
 *    όπως σε κάθε «Φύλλο»): το ίδιο PUT …/platforms/:x για κάθε ενεργή πλατφόρμα που άλλαξε και μετά το
 *    PUT …/platform-flags {has_efood_lastmile, has_wolt_drive}, όπως ως τώρα η «Αποθήκευση Ρυθμίσεων».
 *  - κωδικός και κλειδί δεν έρχονται ποτέ από τον server (από 26/10/2026, μόνο has_password / has_api_key):
 *    το πεδίο ξεκινά κενό με ένδειξη «αποθηκευμένο», και κενό = μένει το αποθηκευμένο.
 */

type OrderPlatform = 'efood' | 'box' | 'wolt';

const ORDER_PLATFORMS: OrderPlatform[] = ['efood', 'box', 'wolt'];
const PLATFORM_NAMES: Record<OrderPlatform, string> = { efood: 'e-food', box: 'BOX', wolt: 'Wolt' };
const FLAG_KEYS: Record<OrderPlatform, 'platform_efood' | 'platform_box' | 'platform_wolt'> = {
  efood: 'platform_efood',
  box: 'platform_box',
  wolt: 'platform_wolt',
};
const DELIVERY_SECTION = 'platforms-delivery';

// Σύνοψη αντιστοιχίσεων ονομάτων του καταστήματος
// (GET /api/product-mappings/:storeId/name-mappings/summary, spec §8).
interface NameMappingSummary {
  pending: number;
  vat_risk: number;
  unmapped_lines_7d: number;
}

interface PlatformFlags {
  platform_efood: boolean;
  platform_box: boolean;
  platform_wolt: boolean;
  // Cross-platform delivery options (quick toggles)
  has_efood_lastmile: boolean;
  has_wolt_drive: boolean;
  // Credential status (from delivery_credentials table)
  laas_mile_enabled?: boolean;
  has_laas_mile_credentials?: boolean;
  wolt_drive_enabled?: boolean;
  has_wolt_drive_credentials?: boolean;
}

interface PlatformSettings {
  efood: {
    isEnabled: boolean;
    restaurantId: string;
    apiUsername: string;
    apiPassword: string;
    isSelfDelivery: boolean;
    presetTimes: number[];
  };
  box: {
    isEnabled: boolean;
    restaurantId: string;
    apiUsername: string;  // Company name for BOX
    apiPassword: string;
    apiKey: string;  // API key for BOX authentication
    isSelfDelivery: boolean;
    presetTimes: number[];
  };
  wolt: {
    isEnabled: boolean;
    restaurantId: string;
    apiUsername: string;
    apiPassword: string;
    apiKey: string;  // WOLT-API-KEY for order operations
    isSelfDelivery: boolean;
    presetTimes: number[];
  };
}

type AnyPlatformSettings = PlatformSettings['efood'] & { apiKey?: string };

// Τι έχει ήδη αποθηκευμένο ο server (has_password / has_api_key): μόνο για την ένδειξη στο κενό πεδίο.
type SavedSecrets = Record<OrderPlatform, { password: boolean; apiKey: boolean }>;
const NO_SAVED_SECRETS: SavedSecrets = {
  efood: { password: false, apiKey: false },
  box: { password: false, apiKey: false },
  wolt: { password: false, apiKey: false },
};

const DEFAULT_SETTINGS: PlatformSettings = {
  efood: {
    isEnabled: false,
    restaurantId: '',
    // efood uses ONE integrator account chain-wide — prefill it so enabling
    // the platform on a new store is restaurant-id-only.
    apiUsername: EFOOD_INTEGRATOR.username,
    apiPassword: EFOOD_INTEGRATOR.password,
    isSelfDelivery: false,
    presetTimes: [15, 20, 30, 45, 60, 90]
  },
  box: {
    isEnabled: false,
    restaurantId: '',
    apiUsername: '',
    apiPassword: '',
    apiKey: '',
    isSelfDelivery: false,
    presetTimes: [15, 20, 30, 45, 60, 90]
  },
  wolt: {
    isEnabled: false,
    restaurantId: '',
    apiUsername: '',
    apiPassword: '',
    apiKey: '',
    isSelfDelivery: false,
    presetTimes: [15, 20, 30, 45, 60, 90]
  }
};

type LoadState = 'loading' | 'ready' | 'error' | 'forbidden';

const PlatformIntegration: React.FC = () => {
  const { t } = useTranslation(['settings', 'common', 'orders', 'storePlatforms']);
  const { store, platforms } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  // Για ποια πλατφόρμα είναι ανοιχτό το παράθυρο αντιστοίχισης (null = κλειστό).
  const [mappingPlatform, setMappingPlatform] = useState<OrderPlatform | null>(null);
  const [mappingSummary, setMappingSummary] = useState<NameMappingSummary | null>(null);

  // Platform enabled flags (from stores table)
  const [platformFlags, setPlatformFlags] = useState<PlatformFlags>({
    platform_efood: false,
    platform_box: false,
    platform_wolt: false,
    has_efood_lastmile: false,
    has_wolt_drive: false,
    laas_mile_enabled: false,
    has_laas_mile_credentials: false,
    wolt_drive_enabled: false,
    has_wolt_drive_credentials: false
  });

  const [platformSettings, setPlatformSettings] = useState<PlatformSettings>(DEFAULT_SETTINGS);
  // Τα αποθηκευμένα (από τη σύνδεση ή την τελευταία αποθήκευση): η βάση της μπάρας αλλαγών.
  const [savedSettings, setSavedSettings] = useState<PlatformSettings>(DEFAULT_SETTINGS);
  const [savedSecrets, setSavedSecrets] = useState<SavedSecrets>(NO_SAVED_SECRETS);
  // Μετά από επιτυχημένη αποθήκευση: ό,τι στάλθηκε μη κενό είναι πλέον «αποθηκευμένο».
  const markSecretsSaved = (platform: OrderPlatform, payload: { api_password?: string; api_key?: string }) => {
    if (!payload.api_password && !payload.api_key) return;
    setSavedSecrets(prev => ({
      ...prev,
      [platform]: {
        password: prev[platform].password || !!payload.api_password,
        apiKey: prev[platform].apiKey || !!payload.api_key,
      },
    }));
  };

  // Load platform flags and size keywords from store
  const loadPlatformFlags = async () => {
    if (!store?.id) return;
    setLoadState('loading');
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store.id}/platform-flags`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPlatformFlags({
          platform_efood: data.platform_efood || false,
          platform_box: data.platform_box || false,
          platform_wolt: data.platform_wolt || false,
          has_efood_lastmile: data.has_efood_lastmile || false,
          has_wolt_drive: data.has_wolt_drive || false,
          laas_mile_enabled: data.laas_mile_enabled || false,
          has_laas_mile_credentials: data.has_laas_mile_credentials || false,
          wolt_drive_enabled: data.wolt_drive_enabled || false,
          has_wolt_drive_credentials: data.has_wolt_drive_credentials || false
        });
        setLoadState('ready');
      } else {
        // Ως 14/10/2026 μια αποτυχία έδειχνε όλες τις πλατφόρμες «ανενεργές» — και ένας διακόπτης
        // θα «ενεργοποιούσε» μια ήδη ενεργή. Τώρα: καμία ρύθμιση, μόνο «Δοκιμάστε ξανά».
        setLoadState(response.status === 403 ? 'forbidden' : 'error');
      }
    } catch (error) {
      console.error('Error loading platform flags:', error);
      setLoadState('error');
    }
  };

  useEffect(() => {
    loadPlatformFlags();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  // Σύνοψη αντιστοιχίσεων ονομάτων για το σήμα δίπλα στη «Διαχείριση
  // Αντιστοιχίσεων»: πόσα ονόματα πλατφόρμας περιμένουν απόφαση και πόσα
  // κρύβουν ρίσκο ΦΠΑ (π.χ. μπύρα 24% που σήμερα κόβεται 13%).
  // Ξαναδιαβάζεται ΜΟΛΙΣ κλείσει το modal, ώστε να δείχνει το αποτέλεσμα των
  // αλλαγών. Κάθε αποτυχία (404 όσο δεν έχει ανέβει το endpoint, 403, δίκτυο)
  // περνά σιωπηλά: το σήμα είναι βοήθημα, όχι λειτουργία της σελίδας.
  useEffect(() => {
    if (!store?.id || mappingPlatform !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await storeNameMappingsAPI.summary(store.id);
        if (cancelled || !data) return;
        setMappingSummary({
          // Τα COUNT της Postgres φτάνουν ως string μέσα από το node-pg· τα
          // κάνουμε αριθμούς πριν τα συγκρίνουμε και τα δείξουμε.
          pending: Number(data.pending) || 0,
          vat_risk: Number(data.vat_risk) || 0,
          unmapped_lines_7d: Number(data.unmapped_lines_7d) || 0
        });
      } catch {
        if (!cancelled) setMappingSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, [store?.id, mappingPlatform]);

  // Load existing platform settings from auth context
  useEffect(() => {
    if (platforms && platforms.length > 0 && initialLoad) {
      const newPlatformSettings = { ...platformSettings };
      const newSavedSecrets = { ...NO_SAVED_SECRETS };

      platforms.forEach(platform => {

        const platformName = platform.platform_name as keyof PlatformSettings;
        if (newPlatformSettings[platformName]) {
          newPlatformSettings[platformName] = {
            ...newPlatformSettings[platformName],
            isEnabled: true,
            restaurantId: platform.restaurant_id || '',
            // Saved credentials win; efood falls back to the chain-wide
            // integrator account when the stored value is empty.
            apiUsername: platform.api_username || (platformName === 'efood' ? EFOOD_INTEGRATOR.username : ''),
            // Κωδικός/κλειδί δεν έρχονται από τον server: αποθηκευμένο → κενό πεδίο (= μένει ως έχει).
            apiPassword: platform.has_password ? '' : (platformName === 'efood' ? EFOOD_INTEGRATOR.password : ''),
            apiKey: '',
            isSelfDelivery: platform.is_self_delivery || false,
            presetTimes: platform.preset_times || [15, 20, 30, 45, 60, 90]
          };
          newSavedSecrets[platformName] = { password: !!platform.has_password, apiKey: !!platform.has_api_key };
        }
      });

      setPlatformSettings(newPlatformSettings);
      setSavedSettings(newPlatformSettings);
      setSavedSecrets(newSavedSecrets);
      setInitialLoad(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platforms, initialLoad]);

  const isEnabled = (platform: OrderPlatform): boolean => platformFlags[FLAG_KEYS[platform]];

  // Αλλαγές που δεν αποθηκεύτηκαν: μόνο σε ενεργή πλατφόρμα (της ανενεργής τα στοιχεία δεν φαίνονται
  // και δεν σώζονταν ποτέ — όπως πριν, που η «Αποθήκευση» υπήρχε μόνο σε ενεργή).
  const dirtyPlatforms = ORDER_PLATFORMS.filter(p => isEnabled(p) && !sameValue(platformSettings[p], savedSettings[p]));
  const dirty = dirtyPlatforms.length > 0;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  // «Έχεις αλλαγές…» πριν από αλλαγή σελίδας / αποσύνδεση / κλείσιμο (utils/unsavedChanges).
  useEffect(() => registerDirtySource('store-platforms', () => dirtyRef.current), []);

  const handleTogglePlatform = async (platform: OrderPlatform, enabled: boolean) => {
    if (!store?.id) return;

    setLoading(true);
    try {
      const flagKey = `platform_${platform}` as keyof PlatformFlags;

      // Update store platform flag
      await fetch(`${API_BASE_URL}/api/stores/${store.id}/platform-flags`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({ [flagKey]: enabled })
      });

      setPlatformFlags(prev => ({ ...prev, [flagKey]: enabled }));

      // If enabling, ensure platform_credentials record exists.
      // Same secret-preservation rule as the main save flow: don't send empty
      // password/api_key strings, or the backend will overwrite saved secrets.
      if (enabled) {
        const current = platformSettings[platform];
        const togglePayload: any = {
          restaurant_id: current.restaurantId || '',
          api_username: current.apiUsername || '',
          is_self_delivery: current.isSelfDelivery,
          preset_times: current.presetTimes
        };
        const pw = current.apiPassword;
        if (pw && pw.trim() !== '') togglePayload.api_password = pw;
        if (platform === 'wolt' || platform === 'box') {
          const ak = (current as typeof platformSettings.wolt).apiKey;
          if (ak && ak.trim() !== '') togglePayload.api_key = ak;
        }
        await storeAPI.updatePlatform(store.id, platform, togglePayload);
        // Ο server έχει πλέον ό,τι δείχνει η φόρμα αυτής της πλατφόρμας.
        setSavedSettings(prev => ({ ...prev, [platform]: current }));
        markSecretsSaved(platform, togglePayload);
      }

      showToast('success', enabled ? t('settings:platform.enabledSuccess', { platform: platform.toUpperCase() }) : t('settings:platform.disabledSuccess', { platform: platform.toUpperCase() }));
    } catch (error) {
      console.error(`Error toggling ${platform}:`, error);
      showToast('error', enabled ? t('settings:platform.enableError', { platform }) : t('settings:platform.disableError', { platform }));
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformSettingChange = (platform: keyof PlatformSettings, field: string, value: any) => {
    setPlatformSettings(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  const savePlatformSettings = async (platform: OrderPlatform) => {
    if (!store?.id) return;

    const settings = platformSettings[platform];

    try {
      // IMPORTANT: never send empty strings for sensitive fields. The backend treats
      // '' as "user did not change this; keep existing value", so an unchanged
      // password field must NOT appear in the request body. Sending '' here used
      // to overwrite the real saved password with empty, breaking the integration.
      const platformData: any = {
        restaurant_id: settings.restaurantId,
        api_username: settings.apiUsername,
        is_self_delivery: settings.isSelfDelivery,
        // Όχι auto_print_tickets: από 14/10/2026 το γράφουν μόνο οι «Εκτυπωτές» (PUT /printing/switches)·
        // χωρίς το πεδίο ο server κρατά την τιμή του (routes/stores.js) — όχι παλιά τιμή από τη σύνδεση.
        preset_times: settings.presetTimes
      };

      if (settings.apiPassword && settings.apiPassword.trim() !== '') {
        platformData.api_password = settings.apiPassword;
      }

      // Add api_key only if the user actually entered one (Wolt and BOX).
      if ((platform === 'wolt' || platform === 'box') && 'apiKey' in settings) {
        const apiKey = (settings as typeof platformSettings.wolt).apiKey;
        if (apiKey && apiKey.trim() !== '') {
          platformData.api_key = apiKey;
        }
      }

      await storeAPI.updatePlatform(store.id, platform, platformData);
      markSecretsSaved(platform, platformData);
    } catch (error) {
      console.error(`❌ [PLATFORM] Error saving ${platform} settings:`, error);
      throw error;
    }
  };

  // «Αποθήκευση» της μπάρας: κάθε ενεργή πλατφόρμα που άλλαξε (ένα PUT η καθεμία, όπως η παλιά
  // «Αποθήκευση Ρυθμίσεων» της καρτέλας της) και μετά οι ρυθμίσεις παράδοσης τρίτων.
  const handleSave = async () => {
    const targets = dirtyPlatforms;
    const snapshot = platformSettings;
    setLoading(true);

    try {
      for (const platform of targets) {
        // Ένα-ένα: αν αποτύχει κάποια, όσες σώθηκαν δεν ξαναμετρούν ως αλλαγές.
        // eslint-disable-next-line no-await-in-loop
        await savePlatformSettings(platform);
        setSavedSettings(prev => ({ ...prev, [platform]: snapshot[platform] }));
      }

      // Save cross-platform delivery settings
      await fetch(`${API_BASE_URL}/api/stores/${store?.id}/platform-flags`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          has_efood_lastmile: platformFlags.has_efood_lastmile,
          has_wolt_drive: platformFlags.has_wolt_drive
          // Note: Delivery credentials are now managed in dedicated sections (Laas Mile / Wolt Drive)
        })
      });

      showToast('success', t('settings:platform.settingsSaved'));
    } catch (error) {
      console.error('Save settings error:', error);
      showToast('error', t('settings:platform.settingsSaveError'));
    } finally {
      setLoading(false);
    }
  };

  // «Αναίρεση»: πίσω στα αποθηκευμένα.
  const handleReset = () => setPlatformSettings(savedSettings);

  // «Υπηρεσίες παράδοσης →»: ως 14/10/2026 άλλαζε καρτέλα· τώρα πηγαίνει στην ενότητα.
  const goToDeliveryServices = () => {
    const el = document.getElementById(sectionDomId(DELIVERY_SECTION));
    if (!el) return;
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.querySelector<HTMLElement>('[data-sheet-heading]')?.focus({ preventScroll: true });
  };

  const fieldLabels = (platform: OrderPlatform) => ({
    id: platform === 'wolt' ? t('storePlatforms:fields.venueId') : platform === 'box' ? t('storePlatforms:fields.shopId') : t('storePlatforms:fields.restaurantId'),
    idPlaceholder: platform === 'wolt'
      ? t('storePlatforms:fields.venueIdPlaceholder')
      : platform === 'box' ? t('storePlatforms:fields.shopIdPlaceholder') : t('storePlatforms:fields.restaurantIdPlaceholder'),
    user: platform === 'wolt'
      ? t('storePlatforms:fields.basicAuthUsername')
      : platform === 'box' ? t('storePlatforms:fields.companyName') : t('storePlatforms:fields.apiUsername'),
    userPlaceholder: platform === 'wolt'
      ? t('storePlatforms:fields.basicAuthUsernamePlaceholder')
      : platform === 'box' ? t('storePlatforms:fields.companyNamePlaceholder') : t('storePlatforms:fields.apiUsernamePlaceholder'),
    password: platform === 'wolt' ? t('storePlatforms:fields.basicAuthPassword') : t('storePlatforms:fields.apiPassword'),
    passwordPlaceholder: platform === 'wolt' ? t('storePlatforms:fields.basicAuthPasswordPlaceholder') : t('storePlatforms:fields.apiPasswordPlaceholder'),
    key: platform === 'wolt' ? t('storePlatforms:fields.woltApiKey') : t('storePlatforms:fields.apiKey'),
    keyPlaceholder: platform === 'wolt' ? t('storePlatforms:fields.woltApiKeyPlaceholder') : t('storePlatforms:fields.apiKeyPlaceholder'),
    keyHint: platform === 'wolt' ? t('storePlatforms:fields.woltApiKeyHint') : t('storePlatforms:fields.boxApiKeyHint'),
  });

  const renderPlatform = (platform: OrderPlatform) => {
    const name = PLATFORM_NAMES[platform];
    const enabled = isEnabled(platform);
    const current = platformSettings[platform] as AnyPlatformSettings;
    const saved = savedSettings[platform] as AnyPlatformSettings;
    const changed = (field: keyof AnyPlatformSettings) => !sameValue(current[field], saved[field]);
    const labels = fieldLabels(platform);
    const idPrefix = `platform-${platform}`;
    const setField = (field: string, value: unknown) => handlePlatformSettingChange(platform, field, value);

    return (
      <SheetSection
        key={platform}
        id={idPrefix}
        title={name}
        hint={enabled ? t('settings:platform.platformActive') : t('settings:platform.platformInactiveHint')}
        dirty={dirtyPlatforms.includes(platform)}
        testId={`platform-section-${platform}`}
      >
        {/* Τι κάνει ο διακόπτης το λέει ήδη η εξήγηση της ενότητας (ενεργή / ενεργοποιήστε για…). */}
        <SwitchRow
          title={t('settings:platform.integration', { name })}
          checked={enabled}
          onChange={next => handleTogglePlatform(platform, next)}
          disabled={loading}
        />

        {enabled && (
          <div className="mt-6 space-y-6">
            <div className="xs-grid2">
              <SheetField label={labels.id} htmlFor={`${idPrefix}-id`} changed={changed('restaurantId')}>
                <input
                  id={`${idPrefix}-id`}
                  type="text"
                  value={current.restaurantId}
                  onChange={(e) => setField('restaurantId', e.target.value)}
                  autoComplete="one-time-code"
                  spellCheck={false}
                  className={INPUT_CLS}
                  placeholder={labels.idPlaceholder}
                />
              </SheetField>

              <SheetField
                label={labels.user}
                htmlFor={`${idPrefix}-user`}
                hint={platform === 'box' ? t('storePlatforms:fields.companyHint') : undefined}
                changed={changed('apiUsername')}
              >
                <input
                  id={`${idPrefix}-user`}
                  type="text"
                  value={current.apiUsername}
                  onChange={(e) => setField('apiUsername', e.target.value)}
                  autoComplete="one-time-code"
                  spellCheck={false}
                  className={INPUT_CLS}
                  placeholder={labels.userPlaceholder}
                />
              </SheetField>

              {/* Password field - not needed for BOX */}
              {platform !== 'box' && (
                <SheetField label={labels.password} htmlFor={`${idPrefix}-password`} changed={changed('apiPassword')}>
                  <input
                    id={`${idPrefix}-password`}
                    type="password"
                    value={current.apiPassword}
                    onChange={(e) => setField('apiPassword', e.target.value)}
                    autoComplete="one-time-code"
                    className={INPUT_CLS}
                    placeholder={savedSecrets[platform].password ? t('storePlatforms:fields.savedSecretPlaceholder') : labels.passwordPlaceholder}
                  />
                </SheetField>
              )}

              {/* API Key for Wolt and BOX */}
              {(platform === 'wolt' || platform === 'box') && (
                <SheetField label={labels.key} htmlFor={`${idPrefix}-key`} hint={labels.keyHint} changed={changed('apiKey')}>
                  <input
                    id={`${idPrefix}-key`}
                    type="text"
                    value={current.apiKey || ''}
                    onChange={(e) => setField('apiKey', e.target.value)}
                    autoComplete="one-time-code"
                    spellCheck={false}
                    className={`${INPUT_CLS} font-mono`}
                    placeholder={savedSecrets[platform].apiKey ? t('storePlatforms:fields.savedSecretPlaceholder') : labels.keyPlaceholder}
                  />
                </SheetField>
              )}
            </div>

            <div>
              <SheetRow
                title={t('settings:platform.selfDelivery')}
                subtitle={t('settings:platform.selfDeliveryDesc')}
                changed={changed('isSelfDelivery')}
                control={({ labelId, descriptionId }) => (
                  <SettingsSwitch
                    checked={current.isSelfDelivery}
                    onChange={next => setField('isSelfDelivery', next)}
                    labelId={labelId}
                    describedById={descriptionId}
                  />
                )}
              />
              {/* Courier τρίτων — μόνο με δική σας παράδοση (όπως πριν) */}
              {current.isSelfDelivery && (
                <div className="mt-3 text-sm" data-testid={`${idPrefix}-third-party`}>
                  <p className="font-medium text-t1">{t('settings:platform.thirdPartyServices')}</p>
                  <p className="mt-0.5 text-t3">{t('settings:platform.thirdPartyDesc')}</p>
                  <ul className="mt-2 space-y-1.5">
                    <li className="flex flex-wrap items-center gap-2">
                      <span className="text-t2">Laas Mile (efood)</span>
                      <StatusChip tone={platformFlags.laas_mile_enabled ? 'ok' : 'muted'}>
                        {platformFlags.laas_mile_enabled ? t('common:enabled') : t('common:disabled')}
                      </StatusChip>
                    </li>
                    <li className="flex flex-wrap items-center gap-2">
                      <span className="text-t2">Wolt Drive</span>
                      <StatusChip tone={platformFlags.wolt_drive_enabled ? 'ok' : 'muted'}>
                        {platformFlags.wolt_drive_enabled ? t('common:enabled') : t('common:disabled')}
                      </StatusChip>
                    </li>
                  </ul>
                  <p className="xs-note">
                    {t('storePlatforms:thirdPartyNote')}{' '}
                    <button type="button" className="xs-link" onClick={goToDeliveryServices}>{t('storePlatforms:goToDeliveryServices')}</button>
                  </p>
                </div>
              )}
            </div>

            <SheetField
              label={t('settings:platform.deliveryTimeOptions')}
              hint={t('settings:platform.deliveryTimeDesc', { name })}
              changed={changed('presetTimes')}
            >
              <div className="flex flex-wrap items-center gap-2" data-testid={`${idPrefix}-times`}>
                {current.presetTimes.map((time: number, index: number) => (
                  <span key={index} className="inline-flex items-center gap-1 h-11 pl-1.5 pr-1 rounded-xl border border-line-strong bg-surface-2">
                    <input
                      type="number"
                      value={time}
                      onChange={(e) => {
                        const newTimes = [...current.presetTimes];
                        newTimes[index] = parseInt(e.target.value) || 0;
                        setField('presetTimes', newTimes);
                      }}
                      aria-label={t('storePlatforms:timeLabel', { n: index + 1 })}
                      className="w-14 h-8 px-1 rounded-lg border border-line bg-surface-1 text-t1 text-center text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring"
                      min="1"
                      max="999"
                    />
                    <span className="text-sm text-t3">{t('storePlatforms:minutes')}</span>
                    <button
                      type="button"
                      onClick={() => setField('presetTimes', current.presetTimes.filter((_: number, i: number) => i !== index))}
                      className="w-8 h-8 rounded-lg text-t3 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors"
                      aria-label={t('storePlatforms:removeTime', { n: time })}
                      title={t('storePlatforms:removeTime', { n: time })}
                    >
                      <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => setField('presetTimes', [...current.presetTimes, 30])}
                  className={ASIDE_BTN}
                >
                  <Plus className="w-4 h-4" aria-hidden="true" />
                  {t('settings:platform.addTime')}
                </button>
              </div>
            </SheetField>
          </div>
        )}
      </SheetSection>
    );
  };

  const enabledPlatforms = ORDER_PLATFORMS.filter(isEnabled);

  // Κόκκινο όταν υπάρχει ρίσκο ΦΠΑ (λάθος συντελεστής στην απόδειξη), πορτοκαλί όταν απλώς
  // εκκρεμούν αποφάσεις. Ένα για όλο το κατάστημα (ως 14/10 φαινόταν σε κάθε καρτέλα, ίδιο).
  const mappingBadge = mappingSummary && (mappingSummary.vat_risk > 0 || mappingSummary.pending > 0) ? (
    <StatusChip
      tone={mappingSummary.vat_risk > 0 ? 'danger' : 'warn'}
      testId="mapping-badge"
      title={t('orders:mapping.badgeTitle', {
        pending: mappingSummary.pending,
        vatRisk: mappingSummary.vat_risk,
        lines: mappingSummary.unmapped_lines_7d,
        defaultValue: 'Ονόματα πλατφόρμας: {{pending}} σε εκκρεμότητα, {{vatRisk}} με ρίσκο ΦΠΑ, {{lines}} γραμμές χωρίς αντιστοίχιση τις τελευταίες 7 ημέρες.'
      })}
    >
      {mappingSummary.vat_risk > 0
        ? t('orders:mapping.badgeVatRisk', { n: mappingSummary.vat_risk, defaultValue: 'ΦΠΑ: {{n}}' })
        : t('orders:mapping.badgePending', { n: mappingSummary.pending, defaultValue: 'Εκκρεμούν: {{n}}' })}
    </StatusChip>
  ) : null;

  return (
    <>
      <SettingsSheet eyebrow={t('settings:nav.groups.catalog')} title={t('settings:platform.title')} subtitle={t('settings:platform.subtitle')}>
        {loadState === 'loading' ? (
          <SheetBlock><StoreLoadState status="loading" onRetry={loadPlatformFlags} /></SheetBlock>
        ) : loadState !== 'ready' ? (
          <SheetBlock>
            <LoadFailed
              message={loadState === 'forbidden' ? t('storePlatforms:forbidden') : t('storePlatforms:loadFailed')}
              onRetry={loadPlatformFlags}
            />
          </SheetBlock>
        ) : (
          <>
            <fieldset disabled={loading} className="min-w-0">
              {ORDER_PLATFORMS.map(renderPlatform)}

              {/* Product Mapping — για κάθε ενεργή πλατφόρμα παραγγελιών */}
              <SheetSection
                id="platforms-mapping"
                title={t('settings:platform.productMapping')}
                hint={t('settings:platform.productMappingDesc')}
                aside={mappingBadge}
                testId="platform-mapping-section"
              >
                {enabledPlatforms.length === 0 ? (
                  <p className="text-sm text-t3">{t('storePlatforms:mappingNone')}</p>
                ) : enabledPlatforms.map(platform => (
                  <SheetRow
                    key={platform}
                    title={PLATFORM_NAMES[platform]}
                    control={(
                      <button type="button" onClick={() => setMappingPlatform(platform)} className={GHOST_BTN}>
                        <Link2 className="w-4 h-4" aria-hidden="true" />
                        {t('settings:platform.manageMappings')}
                      </button>
                    )}
                  />
                ))}
                <div className="mt-6 text-sm">
                  <p className="font-medium text-t2">{t('settings:platform.whyMapProducts')}</p>
                  <ul className="mt-1.5 list-disc space-y-1 pl-5 text-t3">
                    <li>{t('settings:platform.mapBenefit1')}</li>
                    <li>{t('settings:platform.mapBenefit2')}</li>
                    <li>{t('settings:platform.mapBenefit3')}</li>
                  </ul>
                </div>
              </SheetSection>
            </fieldset>

            {/* Υπηρεσίες παράδοσης (ως 14/10/2026 δύο καρτέλες): οι ίδιες ρυθμίσεις, με δική τους αποθήκευση. */}
            <SheetSection
              id={DELIVERY_SECTION}
              title={t('storePlatforms:deliveryServices')}
              hint={t('storePlatforms:deliveryServicesHint')}
              testId="platform-delivery-section"
            >
              <div className="space-y-3">
                <LaasMileConfig />
                <WoltDriveConfig />
              </div>
            </SheetSection>
          </>
        )}

        <UnsavedChangesBar dirty={loadState === 'ready' && dirty} saving={loading} onSave={handleSave} onReset={handleReset} />
      </SettingsSheet>

      {/* Product Mapping Modal - only for order platforms */}
      <ProductMappingModal
        isOpen={mappingPlatform !== null}
        onClose={() => setMappingPlatform(null)}
        platform={mappingPlatform ?? 'efood'}
      />
    </>
  );
};

export default PlatformIntegration;
